import { useMemo, useRef } from 'react';
import { css, type Theme } from '@emotion/react';
import { Copy, Info, Link } from '@phosphor-icons/react';
import type { ComponentConfigConnectionString } from 'common/stack/ComponentConfigDocuments';
import { replaceVariablesInString } from 'common/stack/stackEnvUtils';
import { useValueCopy } from 'client/components/Input/inputUtils';
import { Tag } from 'client/components/Tag';
import { Tooltip } from 'client/components/Tooltip';
import { Typography } from 'client/components/Typography';

interface ComponentConnectionStringProps {
  connectionString: ComponentConfigConnectionString;
  variables: {
    [key: string]: string | number | boolean;
  };
}

export const ComponentConnectionString = ({ connectionString, variables }: ComponentConnectionStringProps) => {
  const valueRef = useRef<HTMLSpanElement | null>(null);
  const value = useMemo(() => replaceVariablesInString({ value: connectionString.value, variables }), [connectionString.value, variables]);
  const { isCopyTooltipOpen, handleCopy } = useValueCopy({ value, elementRef: valueRef });

  return (
    <>
      <Typography variant="body2" css={titleStyle}>
        <Link weight="bold" /> {connectionString.name}
      </Typography>
      <Tooltip open={isCopyTooltipOpen} title="Text copied to clipboard" align="start" side="right">
        <Tag type="grey" onClick={handleCopy} css={tagStyle}>
          <span ref={valueRef} css={valueStyle}>
            {value}
          </span>
          <Copy />
        </Tag>
      </Tooltip>
      <Typography variant="body2" css={helpStyle}>
        <Info /> Only accessible internally
      </Typography>
    </>
  );
};

const tagStyle = (theme: Theme) => css`
  padding: 2px 6px;
  min-height: 24px;
  gap: 5px;
  background-color: ${theme.color.grey[60]};
  color: ${theme.color.grey[100]} !important;
  cursor: pointer;
`;

const titleStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: bold !important;
  margin-bottom: 4px;
`;

const valueStyle = css`
  user-select: all;
`;

const helpStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0.6;

  > svg {
    transform: translateY(0.5px);
  }
`;
